import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, AlertTriangle, XCircle, LayoutGrid, List } from 'lucide-react';
import { Card } from '../../../../../components/ui/Card';
import { Badge } from '../../../../../components/ui/Badge';
import { FilterBar, ExportMenu, MODULE_CONFIG } from '../shared';
import { Select } from '../../../../../components/ui/Select';
import { QuestionDetailPanel } from './QuestionDetailPanel';
import type { UnifiedQuestionRow } from '../hooks/useGovernanceData';
import type { PipelineBatchResult } from '../../../../../governance';
import { QUESTION_TYPE_REGISTRY } from '../../../../../content/questions/questionTypeRegistry';
interface QuestionsTabProps {
  rows: UnifiedQuestionRow[];
  batchResult: PipelineBatchResult | null;
  isRunning: boolean;
  initialFilter?: string;
}
type VerdictFilter = 'all' | 'PASS' | 'REVIEW_NEEDED' | 'FAIL';
type IssueFilter = 'all' | 'blockers' | 'warnings' | 'info';
type ViewMode = 'cards' | 'table';
export function QuestionsTab({
  rows,
  batchResult,
  isRunning,
  initialFilter
}: QuestionsTabProps) {
  const [search, setSearch] = useState(initialFilter && initialFilter !== 'blockers' ? initialFilter : '');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>(initialFilter === 'blockers' ? 'FAIL' : 'all');
  const [issueFilter, setIssueFilter] = useState<IssueFilter>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  // Quick filters
  const [onlySeedIssues, setOnlySeedIssues] = useState(false);
  const [onlyVisualIssues, setOnlyVisualIssues] = useState(false);
  const [onlyPedagogyIssues, setOnlyPedagogyIssues] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Pagination
  const [page, setPage] = useState(1);
  const itemsPerPage = 50;
  const filteredRows = useMemo(() => {
    return rows.filter(row => {
      // Search
      if (search && !row.questionId.toLowerCase().includes(search.toLowerCase()) && !row.familyId.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      // Module
      if (moduleFilter !== 'all' && row.moduleCode !== moduleFilter) {
        return false;
      }
      // Verdict
      if (verdictFilter !== 'all' && row.verdict !== verdictFilter) {
        return false;
      }
      // Issue Type
      if (issueFilter === 'blockers' && row.blockerCount === 0) return false;
      if (issueFilter === 'warnings' && row.warningCount === 0) return false;
      if (issueFilter === 'info' && row.pipelineIssues.filter(i => i.kind === 'info').length === 0) return false;
      // Question Type
      if (typeFilter !== 'all' && row.questionType !== typeFilter) {
        return false;
      }
      // Quick Filters
      if (onlySeedIssues && row.seedWarnings.length === 0) return false;
      if (onlyVisualIssues && !row.hasVisualProblems) return false;
      if (onlyPedagogyIssues && !row.pipelineIssues.some(i => i.domain === 'pedagogy')) return false;
      return true;
    });
  }, [rows, search, moduleFilter, verdictFilter, issueFilter, typeFilter, onlySeedIssues, onlyVisualIssues, onlyPedagogyIssues]);
  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [search, moduleFilter, verdictFilter, issueFilter, typeFilter, onlySeedIssues, onlyVisualIssues, onlyPedagogyIssues]);
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return filteredRows.slice(start, start + itemsPerPage);
  }, [filteredRows, page]);
  const totalPages = Math.ceil(filteredRows.length / itemsPerPage);
  const passCount = filteredRows.filter(r => r.verdict === 'PASS').length;
  const failCount = filteredRows.filter(r => r.verdict === 'FAIL').length;
  const reviewCount = filteredRows.filter(r => r.verdict === 'REVIEW_NEEDED').length;
  if (isRunning) {
    return <div className="py-12 flex flex-col items-center justify-center space-y-4" data-id="element-4182">
        <div className="w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" data-id="element-4183"></div>
        <p className="text-gray-500 font-medium" data-id="element-4184">Data laden en valideren...</p>
      </div>;
  }
  return <div className="space-y-4" data-id="element-4185">
      {/* Filters */}
      <FilterBar moduleFilter={moduleFilter} onModuleFilter={setModuleFilter} search={search} onSearch={setSearch} data-id="element-4186">
        <Select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} options={[{
        value: 'all',
        label: 'Alle vraagtypes'
      }, ...Object.keys(QUESTION_TYPE_REGISTRY).map(t => ({
        value: t,
        label: t
      }))]} size="sm" fullWidth={false} data-id="element-4187" />
        <Select value={verdictFilter} onChange={e => setVerdictFilter(e.target.value as VerdictFilter)} options={[{
        value: 'all',
        label: 'Alle statussen'
      }, {
        value: 'PASS',
        label: 'Geslaagd (PASS)'
      }, {
        value: 'REVIEW_NEEDED',
        label: 'Review (REVIEW)'
      }, {
        value: 'FAIL',
        label: 'Afgekeurd (FAIL)'
      }]} size="sm" fullWidth={false} data-id="element-4188" />
        <Select value={issueFilter} onChange={e => setIssueFilter(e.target.value as IssueFilter)} options={[{
        value: 'all',
        label: 'Alle bevindingen'
      }, {
        value: 'blockers',
        label: 'Heeft blockers'
      }, {
        value: 'warnings',
        label: 'Heeft verbeterpunten'
      }]} size="sm" fullWidth={false} data-id="element-4189" />
      </FilterBar>

      {/* Quick Filters & Summary */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3 rounded-xl border border-gray-200 shadow-sm" data-id="element-4190">
        <div className="flex flex-wrap items-center gap-2" data-id="element-4191">
          <button onClick={() => setOnlySeedIssues(!onlySeedIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlySeedIssues ? 'bg-amber-100 text-amber-800 border-amber-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-4192">
            Seed Issues
          </button>
          <button onClick={() => setOnlyVisualIssues(!onlyVisualIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlyVisualIssues ? 'bg-purple-100 text-purple-800 border-purple-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-4193">
            Visual Issues
          </button>
          <button onClick={() => setOnlyPedagogyIssues(!onlyPedagogyIssues)} className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors border ${onlyPedagogyIssues ? 'bg-indigo-100 text-indigo-800 border-indigo-200' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`} data-id="element-4194">
            Pedagogie Issues
          </button>

          <div className="h-4 w-px bg-gray-300 mx-1 hidden sm:block" data-id="element-4195"></div>

          <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-id="element-4196">
            <button onClick={() => setViewMode('table')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title="Tabelweergave" data-id="element-4197">
              <List className="w-4 h-4" data-id="element-4198" />
            </button>
            <button onClick={() => setViewMode('cards')} className={`p-1.5 rounded-md transition-colors ${viewMode === 'cards' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'}`} title="Kaartweergave" data-id="element-4199">
              <LayoutGrid className="w-4 h-4" data-id="element-4200" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm" data-id="element-4201">
          <div className="flex items-center gap-3 text-gray-600" data-id="element-4202">
            <span className="font-semibold text-gray-900" data-id="element-4203">
              {filteredRows.length}
            </span>{' '}
            vragen
            <span className="text-emerald-600 font-medium" data-id="element-4204">
              {passCount} geslaagd
            </span>
            <span className="text-red-600 font-medium" data-id="element-4205">
              {failCount} afgekeurd
            </span>
            <span className="text-amber-600 font-medium" data-id="element-4206">
              {reviewCount} review
            </span>
          </div>

          {batchResult && <ExportMenu results={batchResult.results.filter(r => filteredRows.some(fr => fr.questionId === r.questionId))} filePrefix="governance-vragen" data-id="element-4207" />}
        </div>
      </div>

      {/* Table / Cards */}
      <div className="space-y-2" data-id="element-4208">
        {paginatedRows.length === 0 ? <div className="rounded-xl border border-gray-200 bg-white py-12 text-center" data-id="element-4209">
            <p className="text-gray-500" data-id="element-4210">
              Geen vragen gevonden voor deze filters.
            </p>
          </div> : viewMode === 'table' ? <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-4211">
            <div className="overflow-x-auto" data-id="element-4212">
              <table className="w-full text-left text-sm" data-id="element-4213">
                <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 font-medium" data-id="element-4214">
                  <tr data-id="element-4215">
                    <th className="px-4 py-3" data-id="element-4216">ID</th>
                    <th className="px-4 py-3" data-id="element-4217">Status</th>
                    <th className="px-4 py-3" data-id="element-4218">Score</th>
                    <th className="px-4 py-3" data-id="element-4219">Issues</th>
                    <th className="px-4 py-3" data-id="element-4220">Type</th>
                    <th className="px-4 py-3" data-id="element-4221">Acties</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-4222">
                  {paginatedRows.map(row => {
                const isExpanded = expandedId === row.questionId;
                const modConfig = MODULE_CONFIG[row.moduleCode] || {
                  bgClass: 'bg-gray-500'
                };
                const statusConfig = {
                  PASS: {
                    bg: 'bg-emerald-50',
                    text: 'text-emerald-700',
                    icon: CheckCircle2,
                    label: 'PASS'
                  },
                  REVIEW_NEEDED: {
                    bg: 'bg-amber-50',
                    text: 'text-amber-700',
                    icon: AlertTriangle,
                    label: 'REVIEW'
                  },
                  FAIL: {
                    bg: 'bg-red-50',
                    text: 'text-red-700',
                    icon: XCircle,
                    label: 'FAIL'
                  }
                }[row.verdict];
                const StatusIcon = statusConfig.icon;
                return <Fragment key={row.questionId} data-id="element-4223">
                        <tr className={`hover:bg-gray-50 cursor-pointer transition-colors ${isExpanded ? 'bg-blue-50/30' : ''}`} onClick={() => setExpandedId(isExpanded ? null : row.questionId)} data-id="element-4224">
                          <td className="px-4 py-3" data-id="element-4225">
                            <div className="flex items-center gap-2" data-id="element-4226">
                              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${modConfig.bgClass}`} data-id="element-4227" />
                              <span className="font-mono font-semibold text-gray-900" data-id="element-4228">
                                {row.questionId}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-4229">
                            <Badge variant={row.verdict === 'PASS' ? 'success' : row.verdict === 'REVIEW_NEEDED' ? 'warning' : 'danger'} size="sm" data-id="element-4230">
                              <StatusIcon className="w-3 h-3 mr-1 inline" data-id="element-4231" />
                              {statusConfig.label}
                            </Badge>
                          </td>
                          <td className="px-4 py-3" data-id="element-4232">
                            <div className="flex items-center gap-2 w-24" data-id="element-4233">
                              <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4234">
                                <div className={`h-full rounded-full ${row.pipelineScore >= 80 ? 'bg-emerald-500' : row.pipelineScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                            width: `${row.pipelineScore}%`
                          }} data-id="element-4235" />
                              </div>
                              <span className="text-xs font-medium text-gray-600 w-8 text-right" data-id="element-4236">
                                {row.pipelineScore}%
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-4237">
                            <div className="flex items-center gap-1.5 text-xs" data-id="element-4238">
                              {row.blockerCount > 0 && <span className="flex items-center gap-1 text-red-600 font-medium bg-red-50 px-1.5 py-0.5 rounded" data-id="element-4239">
                                  <XCircle className="w-3 h-3" data-id="element-4240" />{' '}
                                  {row.blockerCount}
                                </span>}
                              {row.warningCount > 0 && <span className="flex items-center gap-1 text-amber-600 font-medium bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-4241">
                                  <AlertTriangle className="w-3 h-3" data-id="element-4242" />{' '}
                                  {row.warningCount}
                                </span>}
                              {row.blockerCount === 0 && row.warningCount === 0 && <span className="text-gray-400" data-id="element-4243">-</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3" data-id="element-4244">
                            <span className="font-mono text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-4245">
                              {row.questionType}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right" data-id="element-4246">
                            {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400 inline" data-id="element-4247" /> : <ChevronRight className="w-4 h-4 text-gray-400 inline" data-id="element-4248" />}
                          </td>
                        </tr>
                        {isExpanded && <tr data-id="element-4249">
                            <td colSpan={6} className="p-0 border-t border-gray-100" data-id="element-4250">
                              <QuestionDetailPanel row={row} data-id="element-4251" />
                            </td>
                          </tr>}
                      </Fragment>;
              })}
                </tbody>
              </table>
            </div>
          </div> : paginatedRows.map(row => {
        const isExpanded = expandedId === row.questionId;
        const modConfig = MODULE_CONFIG[row.moduleCode] || {
          bgClass: 'bg-gray-500'
        };
        const statusConfig = {
          PASS: {
            bg: 'bg-emerald-50',
            border: 'border-emerald-200',
            text: 'text-emerald-700',
            icon: CheckCircle2,
            label: 'PASS'
          },
          REVIEW_NEEDED: {
            bg: 'bg-amber-50',
            border: 'border-amber-200',
            text: 'text-amber-700',
            icon: AlertTriangle,
            label: 'REVIEW'
          },
          FAIL: {
            bg: 'bg-red-50',
            border: 'border-red-200',
            text: 'text-red-700',
            icon: XCircle,
            label: 'FAIL'
          }
        }[row.verdict];
        const StatusIcon = statusConfig.icon;
        return <Card key={row.questionId} className={`overflow-hidden transition-colors border ${isExpanded ? 'border-blue-300 shadow-md' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-4252">
                <div className="px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 cursor-pointer bg-white" onClick={() => setExpandedId(isExpanded ? null : row.questionId)} data-id="element-4253">
                  {/* Left: ID & Status */}
                  <div className="flex items-center gap-3 min-w-0" data-id="element-4254">
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${modConfig.bgClass}`} data-id="element-4255" />
                    <div className="flex items-center gap-3 min-w-0" data-id="element-4256">
                      <span className="font-mono text-sm font-semibold text-gray-900 truncate" data-id="element-4257">
                        {row.questionId}
                      </span>
                      <Badge variant={row.verdict === 'PASS' ? 'success' : row.verdict === 'REVIEW_NEEDED' ? 'warning' : 'danger'} size="sm" data-id="element-4258">
                        <StatusIcon className="w-3 h-3 mr-1 inline" data-id="element-4259" />
                        {statusConfig.label}
                      </Badge>
                    </div>
                  </div>

                  {/* Right: Score & Issues */}
                  <div className="flex items-center gap-4 sm:gap-6 flex-shrink-0" data-id="element-4260">
                    {/* Issues Summary */}
                    <div className="flex items-center gap-2 text-xs" data-id="element-4261">
                      {row.blockerCount > 0 && <span className="flex items-center gap-1 text-red-600 font-medium bg-red-50 px-1.5 py-0.5 rounded" data-id="element-4262">
                          <XCircle className="w-3 h-3" data-id="element-4263" /> {row.blockerCount}
                        </span>}
                      {row.warningCount > 0 && <span className="flex items-center gap-1 text-amber-600 font-medium bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-4264">
                          <AlertTriangle className="w-3 h-3" data-id="element-4265" />{' '}
                          {row.warningCount}
                        </span>}
                      {row.seedWarnings.length > 0 && <span className="flex items-center gap-1 text-purple-600 font-medium bg-purple-50 px-1.5 py-0.5 rounded" title="Seed warnings" data-id="element-4266">
                          🌱 {row.seedWarnings.length}
                        </span>}
                    </div>

                    {/* Score Bar */}
                    <div className="hidden sm:flex items-center gap-2 w-24" data-id="element-4267">
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden" data-id="element-4268">
                        <div className={`h-full rounded-full ${row.pipelineScore >= 80 ? 'bg-emerald-500' : row.pipelineScore >= 60 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                    width: `${row.pipelineScore}%`
                  }} data-id="element-4269" />
                      </div>
                      <span className="text-xs font-medium text-gray-600 w-8 text-right" data-id="element-4270">
                        {row.pipelineScore}%
                      </span>
                    </div>

                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" data-id="element-4271" /> : <ChevronRight className="w-4 h-4 text-gray-400" data-id="element-4272" />}
                  </div>
                </div>

                {/* Expanded Detail Panel */}
                {isExpanded && <QuestionDetailPanel row={row} data-id="element-4273" />}
              </Card>;
      })}
      </div>

      {/* Pagination */}
      {totalPages > 1 && <div className="flex items-center justify-between bg-white px-4 py-3 rounded-xl border border-gray-200" data-id="element-4274">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4275">
            Vorige
          </button>
          <span className="text-sm text-gray-600" data-id="element-4276">
            Pagina <span className="font-semibold text-gray-900" data-id="element-4277">{page}</span>{' '}
            van {totalPages}
          </span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-4278">
            Volgende
          </button>
        </div>}
    </div>;
}